import { Component, OnInit, OnDestroy } from '@angular/core';
import { Team, TeamAdapter } from '../../models/team';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { from, map, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TeamsService } from '../../services/teams.service';
import { EventsService } from '../../services/events.service';
import { Event, EventAdapter } from '../../models/event';
import { People } from '../../models/people';
import { Skill } from '../../models/skill'; 
import { TranslatePipe } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import { ITeamRecommendations } from '../../_interfaces/teamRecommendations';
import { TeamAssignationModalComponent } from '../../team-assignation-modal/team-assignation-modal.component';
import { ITeamAllocation } from '../../_interfaces/teamAllocation';
import { AvatarComponent } from '../../_utils/components/avatar/avatar.component';
import { ITeamRoleRequirement } from '../../_interfaces/teamRoleRequirement';
import { TeamFormComponent } from '../team-form/team-form.component';
import { SelectPeopleModalComponent } from '../../peoples/select-people-modal/select-people-modal.component';
import { PeopleStatusComponent } from '../../_utils/components/people-status/people-status.component';
import { EventsComponent } from '../../_utils/components/events/events.component';
import { ToastService } from '../../services/toast.service';
import { TeamScoreComponent } from '../team-score/team-score.component';
import { PeopleStatusIconComponent } from '../../_utils/components/people-status-icon/people-status-icon.component';
import { getPersonHiringStatusClass, getPersonStatusIconAndMessage } from '../../_utils/people_util';
import { isToday } from 'date-fns';

@Component({
  selector: 'app-team',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslatePipe,
    MarkdownModule,
    TeamAssignationModalComponent,
    AvatarComponent,
    TeamFormComponent,
    SelectPeopleModalComponent,
    PeopleStatusComponent,
    EventsComponent,
    TeamScoreComponent,
    PeopleStatusIconComponent
  ],
  templateUrl: './team.component.html',
  styleUrl: './team.component.scss'
})
export class TeamComponent implements OnInit, OnDestroy {

  private teamObservable!: Observable<Team>;
  private eventObservable!: Observable<Event[]>;
  private destroy$ = new Subject<void>();

  teamId: number = 0;
  team: Team = new Team(); 
  events: Event[] = [];
  teamToEdit: Team | null = null;

  recommendations?: ITeamRecommendations;
  loadingRecommendations = false;
  recommendationsLoaded = false;

  showTeamAssignationModal: boolean = false;
  showTeamForm: boolean = false;
  showConfirmDialog: boolean = false;
  selectedPersonForAssignation: People | null = null;
  contextTeamIdForAssignation: number | null = null;
  showSelectManagerModal: boolean = false;

  //method mapping for template access
  public getPersonHiringStatusClass = getPersonHiringStatusClass;
  public getPersonStatusIconAndMessage = getPersonStatusIconAndMessage;

  constructor(
    private route: ActivatedRoute,
    private teamsService: TeamsService,
    private teamAdapter: TeamAdapter,
    private eventsService: EventsService,
    private eventAdapter: EventAdapter,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.teamId = this.route.snapshot.params['id'];
    this.fetchTeam();
    this.fetchEvents();
    this.fetchRecommendations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchEvents() {
    this.eventObservable = this.eventsService.getTeamEvents(this.teamId).pipe(
      map((data: any[]) => data.map(item => this.eventAdapter.adapt(item))),
      takeUntil(this.destroy$)
    );

    this.eventObservable.subscribe({
      next: (res: Event[]) => {
        this.events = res;
      },
      error: (err) => console.error('[TeamComponent] Error fetching events:', err)
    });
  }

  fetchTeam() {
    this.teamObservable = this.teamsService.getTeam(this.teamId).pipe(
      map((item: any) => this.teamAdapter.adapt(item)),
      takeUntil(this.destroy$)
    );

    this.teamObservable.subscribe({
      next: (res: Team) => {
        this.team = res;
        this.calculateSkillCoverage(); 
        this.calculateRoleCoverage();
      },
      error: (err) => console.error('[TeamComponent] Error fetching team details:', err)
    });
  }

  fetchRecommendations() {
    this.loadingRecommendations = true;
    this.recommendationsLoaded = false;
    this.teamsService.getTeamRecommendations(this.teamId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.recommendations = data;
        this.loadingRecommendations = false;
        this.recommendationsLoaded = true;
      },
      error: (err) => {
        console.error('[TeamComponent] Error fetching recommendations', err);
        this.loadingRecommendations = false;
        this.recommendationsLoaded = true;
      }
    });
  }

  private calculateRoleCoverage(): void {
    if (!this.team || !this.team.roles || !this.team.peoples) {
      return;
    }

    this.team.roles = (this.team.roles as ITeamRoleRequirement[]).map(updatedRoleRequirement => {
      let assignedFte = 0;
      const roleId = updatedRoleRequirement.role.id;

      const today = new Date();
      
      this.team!.peoples!.forEach(person => {
        if (person.role && person.role.id === roleId 
          && new Date(person.hiring_date) < today
          && (person.exit_date === null || new Date(person.exit_date) > today) ) {
          assignedFte += (this.getAllocationRateForCurrentTeam(person) || 0) / 100;
        }
      });

      updatedRoleRequirement.coverageCount = assignedFte;

      // Logic for role coverage status
      if (assignedFte === 0 && updatedRoleRequirement.required_fte > 0) { // Red if not covered
        updatedRoleRequirement.coverageStatus = 'danger';
      } else if (assignedFte > 0 && assignedFte < updatedRoleRequirement.required_fte) { // Yellow if partially covered
        updatedRoleRequirement.coverageStatus = 'warning';
      } else if (assignedFte > updatedRoleRequirement.required_fte) { // Green if over covered
        updatedRoleRequirement.coverageStatus = 'success';
      } else if (assignedFte === updatedRoleRequirement.required_fte) { // No color if fully covered
        updatedRoleRequirement.coverageStatus = 'default';
      } else {
          // fallback for undefined case
          updatedRoleRequirement.coverageStatus = 'secondary';
      }

      return updatedRoleRequirement;
    });
  }

  private calculateSkillCoverage(): void {

    if (!this.team || !this.team.skills || !this.team.peoples) {
      return;
    }

    const today = new Date();

    this.team.skills = (this.team.skills as Skill[]).map(teamSkill => {
      let count = 0;
      this.team!.peoples!.forEach(person => {
        if (person.skills && person.skills.some(s => s.id === teamSkill.id) 
          && new Date(person.hiring_date) < today
          && (person.exit_date === null || new Date(person.exit_date) > today)) {
          count++;
        }
      });
      teamSkill.coverageCount = count;
      if (count >= 2) {
        teamSkill.coverageStatus = 'success';
      } else if (count === 1) {
        teamSkill.coverageStatus = 'warning';
      } else {
        teamSkill.coverageStatus = 'danger';
      }
      return teamSkill;
    });
}

  openTeamAssignationModal(person: People): void {
    this.selectedPersonForAssignation = person;
    this.contextTeamIdForAssignation = this.teamId;
    this.showTeamAssignationModal = true;
  }

  openTeamAssignationModalForNewPerson(): void {
    this.selectedPersonForAssignation = null;
    this.contextTeamIdForAssignation = this.teamId;
    this.showTeamAssignationModal = true;
  }

  handleTeamAssignationUpdate(updatedTeamsOrUndefined: ITeamAllocation[] | undefined): void {
    this.showTeamAssignationModal = false;
    this.selectedPersonForAssignation = null;
    this.contextTeamIdForAssignation = null;

    if (updatedTeamsOrUndefined !== undefined) {
      if (this.teamId) {
        this.fetchTeam();
        this.fetchRecommendations();
      }
    }
  }

  handleFormSubmission(team: Team): void {
      this.teamsService.updateTeam(this.teamAdapter.adaptToApi(team)).subscribe({
        next: () => {
          this.fetchTeam();
          this.hideTeamForm();
          this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
        },
        error: (err) => {
          this.toastService.showError('COMMON.SAVE_ERROR');
        }
      });
    
  }

  editTeam(team: Team): void {
    this.teamToEdit = team; 
    this.showTeamForm = true; 
  }

  onCancelForm(): void {
    this.hideTeamForm(); 
  }

  hideTeamForm(): void {
    this.showTeamForm = false; 
    this.teamToEdit = null; 
  }
  
  getAllocationRateForCurrentTeam(person: People): number {

    if (!person || !person.teams)
      return 0;

    const teamAllocationEntry = person.teams.find((teamAllocation: ITeamAllocation) => teamAllocation.team.id === this.team.id);

    return teamAllocationEntry?.allocation_rate || 0;
  }

  openSelectManagerModal(): void {
    this.showSelectManagerModal = true;
  }

  removeManager(): void {
    if (this.team.manager) {
      this.team.manager = null; // Remove the manager from the team
      this.teamsService.updateTeam(this.teamAdapter.adaptToApi(this.team)).subscribe({
        next: () => {
          this.fetchTeam(); // Refresh the team data
          this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
        },
        error: (err) => {
          console.error('Error during manager removal', err)
          this.toastService.showError('COMMON.UPDATE_ERROR');
        }
      });
    }
  }

  handleManagerSelected(selectedPerson: People | null): void {
    if (selectedPerson) {
      this.team.manager = selectedPerson;
      // save the manager immediatly
      this.teamsService.updateTeam(this.teamAdapter.adaptToApi(this.team)).subscribe({
        next: () => {
          this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
        },
        error: (err) => {
          console.error('Error during manager update', err)
          this.toastService.showError('COMMON.UPDATE_ERROR');
        }
      });

      // Si le formulaire d'édition est ouvert, mettez à jour teamToEdit
      if (this.showTeamForm && this.teamToEdit) {
        this.teamToEdit.manager = selectedPerson;
      }
    }
    this.showSelectManagerModal = false;
  }

  // Nouvelle méthode pour fermer la modale de sélection de manager
  handleSelectManagerModalClosed(): void {
    this.showSelectManagerModal = false;
  }

}